'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { CircleCheckIcon, CircleIcon, SearchIcon } from 'lucide-react';

import { useDebounce } from '~/hooks/useDebounce';
import { cn, createPath } from '~/lib/utils';
import { BlogCategoryFragment } from '~/server/generated/contentful-graphql';

export function SearchBox({
  placeholder,
  categories = [],
  className,
}: {
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  categories?: BlogCategoryFragment[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const q = searchParams.get('q');
  const categoryParam = searchParams.get('category');
  const [searchValue, setSearchValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || '');
  const searchValueRef = useRef(searchValue);
  const selectedCategoryRef = useRef(selectedCategory);
  searchValueRef.current = searchValue;
  selectedCategoryRef.current = selectedCategory;

  useEffect(() => {
    setSearchValue(q || '');
  }, [q]);

  useEffect(() => {
    setSelectedCategory(categoryParam || '');
  }, [categoryParam]);

  const updateUrl = useCallback(() => {
    const newParams = new URLSearchParams();
    if (searchValueRef.current) {
      newParams.set('q', searchValueRef.current);
    }
    if (selectedCategoryRef.current) {
      newParams.set('category', selectedCategoryRef.current);
    }
    router.push(createPath(pathname, newParams), {
      scroll: false,
    });
  }, [pathname, router]);

  useDebounce(updateUrl, 300, [searchValue]);

  const handleCategoryChange = useCallback(
    (newCategory: string) => {
      const newParams = new URLSearchParams();
      if (searchValue) {
        newParams.set('q', searchValue);
      }
      if (newCategory) {
        newParams.set('category', newCategory);
      }
      router.push(createPath(pathname, newParams), { scroll: false });
    },
    [searchValue, pathname, router],
  );

  return (
    <div className={cn('border-asphalt-10 overflow-hidden rounded-xl border', className)}>
      <form onSubmit={(e) => e.preventDefault()} className='flex items-center gap-x-3 p-4 lg:p-6'>
        <button type='submit' className='appearance-none' aria-label='Search' tabIndex={-1}>
          <SearchIcon className='text-muted-foreground size-4 max-lg:-mb-0.5 lg:size-5' strokeWidth={1.5} />
        </button>
        <input
          name='search'
          type='search'
          placeholder={placeholder || 'Search all stories...'}
          autoComplete='off'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className='grow lg:text-xl'
        />
      </form>
      <div className='bg-muted/25 hide-scrollbar flex items-center gap-x-3 overflow-x-auto px-3  py-3 lg:px-6'>
        <div className='label-xs text-muted-foreground uppercase max-lg:sr-only'>Filter By:</div>
        {categories.length > 0 && (
          <div className='flex shrink-0 gap-2'>
            {categories.map((cat) => {
              const title = cat.title ?? '';
              const isActive = selectedCategory === title;

              return (
                <button
                  key={cat.sys.id}
                  type='button'
                  onClick={() => handleCategoryChange(isActive ? '' : title)}
                  className={cn(
                    'font-greycliff-demibold border-asphalt-10 flex h-8 shrink-0 items-center justify-center gap-x-1.5 rounded-full border bg-white pl-3 pr-4 text-xs transition-colors',
                    isActive && 'bg-secondary text-inverted-foreground border-secondary',
                  )}
                >
                  {isActive ? (
                    <CircleCheckIcon className='size-4' />
                  ) : cat.icon?.url ? (
                    <Image
                      src={cat.icon.url}
                      alt={title ? `Icon for ${title}` : 'Category icon'}
                      width={16}
                      height={16}
                      className='size-4  shrink-0 object-contain'
                    />
                  ) : (
                    <CircleIcon className='size-4' />
                  )}

                  {title}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
